import { getFigmaPayloadFromElement } from './exportSelectedElementForFigma';

type WebExportColor = {
    a: number;
    b: number;
    g: number;
    r: number;
};

type WebExportBorderSide = 'bottom' | 'left' | 'right' | 'top';

type WebExportBorder = {
    color: WebExportColor;
    width: number;
};

type WebExportBorderRadii = {
    bottomLeft: number;
    bottomRight: number;
    topLeft: number;
    topRight: number;
};

type WebExportShadow = {
    blur: number;
    color: WebExportColor;
    inset: boolean;
    spread: number;
    x: number;
    y: number;
};

type WebExportNodeStyle = {
    backgroundColor?: WebExportColor;
    borderColor?: WebExportColor;
    borderRadii?: WebExportBorderRadii;
    borders?: Partial<Record<WebExportBorderSide, WebExportBorder>>;
    borderRadius?: number;
    borderWidth?: number;
    color?: WebExportColor;
    fontFamily?: string;
    fontSize?: number;
    fontWeight?: number;
    lineHeight?: number;
    opacity?: number;
    overflowHidden?: boolean;
    shadows?: WebExportShadow[];
    textAlign?: string;
    textTransform?: string;
};

type WebExportPoint = {
    x: number;
    y: number;
};

type WebExportNode = {
    children?: WebExportNode[];
    height: number;
    name: string;
    paintOrder?: 'foreground';
    points?: WebExportPoint[];
    sourceMode?: 'image' | 'mask';
    style: WebExportNodeStyle;
    sourceUrl?: string;
    svg?: string;
    text?: string;
    textAutoResize?: 'fixed' | 'widthAndHeight';
    type: 'frame' | 'polygon' | 'svg' | 'text';
    width: number;
    x: number;
    y: number;
};

type WebExportPayload = {
    generator: 'rio-uikit-demo';
    root: WebExportNode;
    version: 1;
};

const toCssNumber = (value: number) => {
    const roundedValue = Math.round(value * 1000) / 1000;

    return Object.is(roundedValue, -0) ? 0 : roundedValue;
};

const px = (value: number) => `${toCssNumber(value)}px`;

const colorToCss = (color: WebExportColor) => {
    const r = Math.round(color.r * 255);
    const g = Math.round(color.g * 255);
    const b = Math.round(color.b * 255);

    if (color.a >= 1) {
        return `#${[r, g, b].map(value => value.toString(16).padStart(2, '0')).join('')}`;
    }

    return `rgba(${r}, ${g}, ${b}, ${color.a})`;
};

const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const escapeAttribute = escapeHtml;

const quoteFontFamily = (fontFamily: string) => {
    const normalizedFontFamily = fontFamily.trim();

    if (!normalizedFontFamily || normalizedFontFamily.startsWith('"') || normalizedFontFamily.startsWith("'")) {
        return normalizedFontFamily || 'sans-serif';
    }

    return `"${normalizedFontFamily.replace(/"/g, '\\"')}"`;
};

const styleAttribute = (styles: [string, string | number | undefined][]) => {
    const style = styles
        .filter((styleEntry): styleEntry is [string, string | number] => styleEntry[1] !== undefined)
        .map(([property, value]) => `${property}:${value}`)
        .join(';');

    return ` style="${escapeAttribute(style)}"`;
};

const getVisibleBorders = (node: WebExportNode) =>
    Object.entries(node.style.borders || {}).filter((entry): entry is [WebExportBorderSide, WebExportBorder] =>
        Boolean(entry[1].color && entry[1].color.a > 0 && entry[1].width > 0)
    );

const hasVisibleFill = (node: WebExportNode) => Boolean(node.style.backgroundColor && node.style.backgroundColor.a > 0);

const hasVisibleStroke = (node: WebExportNode) => {
    const borders = getVisibleBorders(node);

    return (
        borders.length === 4 &&
        borders.every(([, border]) => {
            const firstBorder = borders[0][1];

            return (
                border.width === firstBorder.width &&
                border.color.r === firstBorder.color.r &&
                border.color.g === firstBorder.color.g &&
                border.color.b === firstBorder.color.b &&
                border.color.a === firstBorder.color.a
            );
        })
    );
};

const isInsetLineShadow = (shadow: WebExportShadow) =>
    shadow.inset && shadow.blur === 0 && shadow.spread === 0 && (shadow.x !== 0 || shadow.y !== 0);

const getCornerRadiusStyle = (node: WebExportNode): [string, string | undefined][] => {
    const radii = node.style.borderRadii;

    if (!radii) {
        return [['border-radius', node.style.borderRadius ? px(node.style.borderRadius) : undefined]];
    }

    return [
        ['border-top-left-radius', px(radii.topLeft)],
        ['border-top-right-radius', px(radii.topRight)],
        ['border-bottom-right-radius', px(radii.bottomRight)],
        ['border-bottom-left-radius', px(radii.bottomLeft)],
    ];
};

const getBoxShadowStyle = (node: WebExportNode) => {
    const shadows = (node.style.shadows || [])
        .filter(shadow => !isInsetLineShadow(shadow))
        .map(shadow =>
            [
                shadow.inset ? 'inset' : '',
                px(shadow.x),
                px(shadow.y),
                px(shadow.blur),
                px(shadow.spread),
                colorToCss(shadow.color),
            ]
                .filter(Boolean)
                .join(' ')
        );

    return shadows.length > 0 ? shadows.join(', ') : undefined;
};

const getUniformBorderStyle = (node: WebExportNode) => {
    if (!hasVisibleStroke(node)) {
        return;
    }

    const border = getVisibleBorders(node)[0][1];

    return `${px(border.width)} solid ${colorToCss(border.color)}`;
};

const getBaseBoxStyles = (node: WebExportNode, isRoot = false): [string, string | number | undefined][] => [
    ['position', isRoot ? 'relative' : 'absolute'],
    ['left', isRoot ? undefined : px(node.x)],
    ['top', isRoot ? undefined : px(node.y)],
    ['width', px(node.width)],
    ['height', px(node.height)],
    ['box-sizing', 'border-box'],
    ['background-color', hasVisibleFill(node) ? colorToCss(node.style.backgroundColor as WebExportColor) : undefined],
    ['border', getUniformBorderStyle(node)],
    ['box-shadow', getBoxShadowStyle(node)],
    ['opacity', node.style.opacity !== undefined && node.style.opacity < 1 ? node.style.opacity : undefined],
    ['overflow', node.style.overflowHidden ? 'hidden' : undefined],
    ...getCornerRadiusStyle(node),
];

const renderFilledRect = ({
    color,
    height,
    name,
    width,
    x,
    y,
}: {
    color: WebExportColor;
    height: number;
    name: string;
    width: number;
    x: number;
    y: number;
}) =>
    `<div data-rio-uikit-export-node="${escapeAttribute(name)}"${styleAttribute([
        ['position', 'absolute'],
        ['left', px(x)],
        ['top', px(y)],
        ['width', px(width)],
        ['height', px(height)],
        ['box-sizing', 'border-box'],
        ['background-color', colorToCss(color)],
    ])}></div>`;

const renderBorderNodes = (node: WebExportNode) => {
    if (hasVisibleStroke(node)) {
        return '';
    }

    return getVisibleBorders(node)
        .map(([side, border]) => {
            const line = {
                top: {
                    x: node.x,
                    y: node.y,
                    width: node.width,
                    height: border.width,
                },
                right: {
                    x: node.x + node.width - border.width,
                    y: node.y,
                    width: border.width,
                    height: node.height,
                },
                bottom: {
                    x: node.x,
                    y: node.y + node.height - border.width,
                    width: node.width,
                    height: border.width,
                },
                left: {
                    x: node.x,
                    y: node.y,
                    width: border.width,
                    height: node.height,
                },
            }[side];

            return renderFilledRect({
                name: `border ${side}`,
                color: border.color,
                x: line.x,
                y: line.y,
                width: line.width,
                height: line.height,
            });
        })
        .join('');
};

const renderInsetLineShadowNodes = (node: WebExportNode) =>
    (node.style.shadows || [])
        .filter(isInsetLineShadow)
        .map(shadow => {
            if (shadow.y > 0) {
                return renderFilledRect({
                    name: 'inset shadow top',
                    x: node.x,
                    y: node.y,
                    width: node.width,
                    height: shadow.y,
                    color: shadow.color,
                });
            }

            if (shadow.y < 0) {
                return renderFilledRect({
                    name: 'inset shadow bottom',
                    x: node.x,
                    y: node.y + node.height + shadow.y,
                    width: node.width,
                    height: Math.abs(shadow.y),
                    color: shadow.color,
                });
            }

            if (shadow.x > 0) {
                return renderFilledRect({
                    name: 'inset shadow left',
                    x: node.x,
                    y: node.y,
                    width: shadow.x,
                    height: node.height,
                    color: shadow.color,
                });
            }

            return renderFilledRect({
                name: 'inset shadow right',
                x: node.x + node.width + shadow.x,
                y: node.y,
                width: Math.abs(shadow.x),
                height: node.height,
                color: shadow.color,
            });
        })
        .join('');

const renderDecorationNodes = (node: WebExportNode) => `${renderBorderNodes(node)}${renderInsetLineShadowNodes(node)}`;

const renderFrameNode = (node: WebExportNode, isRoot = false) =>
    `<div data-rio-uikit-export-node="${escapeAttribute(node.name)}"${styleAttribute(getBaseBoxStyles(node, isRoot))}></div>`;

const renderTextNode = (node: WebExportNode) =>
    `<div data-rio-uikit-export-node="${escapeAttribute(node.name)}"${styleAttribute([
        ...getBaseBoxStyles(node),
        ['background-color', undefined],
        ['color', node.style.color ? colorToCss(node.style.color) : '#000000'],
        ['font-family', quoteFontFamily(node.style.fontFamily || 'sans-serif')],
        ['font-size', px(node.style.fontSize || 14)],
        ['font-weight', node.style.fontWeight || 400],
        ['line-height', node.style.lineHeight ? px(node.style.lineHeight) : 'normal'],
        ['text-align', node.style.textAlign === 'center' ? 'center' : 'left'],
        ['text-transform', node.style.textTransform],
        ['white-space', node.textAutoResize === 'widthAndHeight' ? 'nowrap' : 'pre-wrap'],
        ['overflow', 'visible'],
    ])}>${escapeHtml(node.text || '')}</div>`;

const normalizeSvgForHtml = (svg: string) =>
    svg
        .replace(/\swidth=(['"])(.*?)\1/i, '')
        .replace(/\sheight=(['"])(.*?)\1/i, '')
        .replace('<svg', '<svg width="100%" height="100%" style="display:block;width:100%;height:100%;"');

const toCssUrl = (url: string) => `url("${url.replace(/"/g, '\\"')}")`;

const renderCdnSvgNode = (node: WebExportNode) =>
    `<div data-rio-uikit-export-node="${escapeAttribute(node.name)}"${styleAttribute([
        ...getBaseBoxStyles(node),
        ['background-color', node.style.color ? colorToCss(node.style.color) : '#000000'],
        ['mask', `${toCssUrl(node.sourceUrl as string)} center / contain no-repeat`],
        ['-webkit-mask', `${toCssUrl(node.sourceUrl as string)} center / contain no-repeat`],
    ])}></div>`;

const renderCdnImageNode = (node: WebExportNode) =>
    `<div data-rio-uikit-export-node="${escapeAttribute(node.name)}"${styleAttribute([
        ...getBaseBoxStyles(node),
        ['background-color', undefined],
        ['background-image', `url("${(node.sourceUrl as string).replace(/"/g, '\\"')}")`],
        ['background-position', 'center'],
        ['background-repeat', 'no-repeat'],
        ['background-size', 'cover'],
    ])}></div>`;

const renderInlineSvgNode = (node: WebExportNode) =>
    `<div data-rio-uikit-export-node="${escapeAttribute(node.name)}"${styleAttribute([
        ...getBaseBoxStyles(node),
        ['background-color', undefined],
        ['overflow', 'visible'],
    ])}>${normalizeSvgForHtml(node.svg || '')}</div>`;

const renderSvgNode = (node: WebExportNode) =>
    node.sourceUrl
        ? node.sourceMode === 'image'
            ? renderCdnImageNode(node)
            : renderCdnSvgNode(node)
        : renderInlineSvgNode(node);

const renderPolygonNode = (node: WebExportNode) => {
    const points = node.points || [];
    const polygon = points
        .map(point => `${toCssNumber((point.x / node.width) * 100)}% ${toCssNumber((point.y / node.height) * 100)}%`)
        .join(', ');

    return `<div data-rio-uikit-export-node="${escapeAttribute(node.name)}"${styleAttribute([
        ...getBaseBoxStyles(node),
        ['background-color', node.style.backgroundColor ? colorToCss(node.style.backgroundColor) : undefined],
        ['clip-path', polygon ? `polygon(${polygon})` : undefined],
    ])}></div>`;
};

const renderNode = (node: WebExportNode) => {
    if (node.type === 'text') {
        return renderTextNode(node);
    }

    if (node.type === 'svg') {
        return renderSvgNode(node);
    }

    if (node.type === 'polygon') {
        return renderPolygonNode(node);
    }

    return `${renderFrameNode(node)}${renderDecorationNodes(node)}`;
};

const renderFlattenedNode = (
    node: WebExportNode,
    foregroundNodes: WebExportNode[] = [],
    deferForeground = true
): string => {
    if (deferForeground && node.paintOrder === 'foreground') {
        foregroundNodes.push(node);
        return '';
    }

    const children = (node.children || [])
        .map(child => renderFlattenedNode(child, foregroundNodes, deferForeground))
        .join('');

    return `${renderNode(node)}${children}`;
};

const renderRoot = (root: WebExportNode) => {
    const foregroundNodes: WebExportNode[] = [];
    const children = (root.children || []).map(child => renderFlattenedNode(child, foregroundNodes)).join('');
    const foregroundChildren = foregroundNodes.map(child => renderFlattenedNode(child, [], false)).join('');

    return `<div data-rio-uikit-web-export="1"${styleAttribute([
        ...getBaseBoxStyles(root, true),
        ['overflow', 'hidden'],
    ])}>${renderDecorationNodes(root)}${children}${foregroundChildren}</div>`;
};

const renderDocument = (body: string) => `<!DOCTYPE html>
<html lang="de">

  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>RIO UIKIT export</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link href="https://fonts.googleapis.com/css2?family=Source+Sans+Pro:wght@300;400;600;700&display=swap" rel="stylesheet" />
  </head>

  <body>
${body}
  </body>

</html>`;

export const getWebHtmlFromElement = async (element: HTMLElement) => {
    const payload = JSON.parse(await getFigmaPayloadFromElement(element)) as WebExportPayload;

    return renderDocument(renderRoot(payload.root));
};
